import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  ColorType,
  createChart,
  LineSeries,
  LineStyle,
  HistogramSeries,
  CrosshairMode,
  IChartApi,
  type CandlestickData,
  type LineData,
  type HistogramData,
  type UTCTimestamp
} from 'lightweight-charts';
import { searchSymbols, type SearchResult } from '../api/searchClient';
import { strategyClient } from '../lib/strategyClient';
import type { Candle, StrategyAnalysis, MarketInterval } from '../types/market';
import { INTERVAL_OPTIONS } from '../lib/intervals';
import { 
  smaSeries, emaSeries, vwapSeriesData, bollingerBandsData, 
  rsiData, stochasticRsiData, macdData, awesomeOscillatorData, 
  mfiData, dmiData, supertrendData 
} from '../lib/indicators';

type ChartType = 'candlestick' | 'line' | 'area' | 'bar' | 'hollow' | 'volume_candles' | 'heikin_ashi';
type IndicatorType = 'sma' | 'ema' | 'vwap' | 'bollinger' | 'rsi' | 'stoch_rsi' | 'macd' | 'ao' | 'mfi' | 'dmi' | 'supertrend';

interface ChartIndicator {
  id: string;
  type: IndicatorType;
  visible: boolean;
  settings: any;
}

interface TradingChartProps {
  symbol: string;
  candles: Candle[];
  analysis?: StrategyAnalysis;
  watchlistSymbols?: string[];
  onAddSymbol?: (sym: string) => void;
  onRemoveSymbol?: (sym: string) => void;
  onSelectSymbol?: (sym: string) => void;
  snapshots?: Record<string, any>;
  interval?: MarketInterval;
  onIntervalChange?: (interval: MarketInterval) => void;
  extendedHours?: boolean;
  onExtendedHoursChange?: (ext: boolean) => void;
}

const DEFAULT_SETTINGS: Record<IndicatorType, any> = {
  sma: { period: 14, color: '#4cc9f0' },
  ema: { period: 14, color: '#e2aa43' },
  vwap: { color: '#f5d06f' },
  bollinger: { period: 20, stdDev: 2, color: '#a78bfa' },
  rsi: { period: 14, color: '#a78bfa', overbought: 70, oversold: 30 },
  stoch_rsi: { rsiPeriod: 14, stochPeriod: 14, k: 3, d: 3, kColor: '#4cc9f0', dColor: '#f05b67' },
  macd: { fast: 12, slow: 26, signal: 9, macdColor: '#4cc9f0', signalColor: '#f05b67', histUp: '#26a69a', histDown: '#ef5350' },
  ao: { fast: 5, slow: 34, upColor: '#26a69a', downColor: '#ef5350' },
  mfi: { period: 14, color: '#e2aa43' },
  dmi: { period: 14, plusColor: '#26a69a', minusColor: '#ef5350', adxColor: '#e2aa43' },
  supertrend: { atrPeriod: 10, multiplier: 3, upColor: '#26a69a', downColor: '#ef5350' }
};

export function TradingChart({ 
  symbol, 
  candles, 
  analysis,
  watchlistSymbols = [],
  onAddSymbol,
  onRemoveSymbol,
  onSelectSymbol,
  snapshots = {},
  interval = '1d',
  onIntervalChange,
  extendedHours = false,
  onExtendedHoursChange
}: TradingChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  const [chartType, setChartType] = useState<ChartType>(() => {
    return (localStorage.getItem('gmv-chart-type') as ChartType) || 'candlestick';
  });
  const [newSymbol, setNewSymbol] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [showPatterns, setShowPatterns] = useState(() => {
    return localStorage.getItem('gmv-chart-patterns') === 'true';
  });
  const mainSeriesRef = useRef<any>(null);
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (!newSymbol.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      const results = await searchSymbols(newSymbol);
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [newSymbol]);

  const [indicators, setIndicators] = useState<ChartIndicator[]>(() => {
    try {
      const stored = localStorage.getItem('gmv-chart-indicators');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return [
      { id: '1', type: 'ema', visible: true, settings: { ...DEFAULT_SETTINGS.ema, period: 9 } },
      { id: '2', type: 'ema', visible: true, settings: { ...DEFAULT_SETTINGS.ema, period: 20 } },
      { id: '3', type: 'rsi', visible: true, settings: { ...DEFAULT_SETTINGS.rsi } },
      { id: '4', type: 'macd', visible: true, settings: { ...DEFAULT_SETTINGS.macd } }
    ];
  });

  useEffect(() => {
    localStorage.setItem('gmv-chart-type', chartType);
  }, [chartType]);

  useEffect(() => {
    localStorage.setItem('gmv-chart-indicators', JSON.stringify(indicators));
  }, [indicators]);

  const supertrendMarkersRef = useRef<any[]>([]);

  const applyMarkers = async (active: boolean) => {
    if (!mainSeriesRef.current || typeof mainSeriesRef.current.setMarkers !== 'function') return;
    
    let combined: any[] = [...supertrendMarkersRef.current];
    
    if (showPatterns && chartType === 'candlestick') {
      try {
        const c = snapshots[symbol]?.data?.candles || [];
        const patternMarkers = await strategyClient.detectPatterns(c);
        if (active) combined = [...combined, ...patternMarkers];
      } catch (err) {
        console.error('Failed to detect patterns:', err);
      }
    }
    
    if (active) {
      combined.sort((a, b) => (a.time as number) - (b.time as number));
      mainSeriesRef.current.setMarkers(combined);
    }
  };

  useEffect(() => {
    let active = true;
    applyMarkers(active);
    localStorage.setItem('gmv-chart-patterns', showPatterns.toString());
    return () => { active = false; };
  }, [showPatterns, chartType, symbol, snapshots, indicators]);
  const [hoveredCandle, setHoveredCandle] = useState<Candle | null>(null);
  const [editingIndicatorId, setEditingIndicatorId] = useState<string | null>(null);

  const addIndicator = (type: IndicatorType) => {
    const id = Date.now().toString();
    setIndicators([...indicators, { id, type, visible: true, settings: { ...DEFAULT_SETTINGS[type] } }]);
  };

  const removeIndicator = (id: string) => {
    setIndicators(indicators.filter((ind) => ind.id !== id));
    if (editingIndicatorId === id) setEditingIndicatorId(null);
  };

  const updateSettings = (id: string, newSettings: any) => {
    setIndicators(indicators.map((ind) => ind.id === id ? { ...ind, settings: { ...ind.settings, ...newSettings } } : ind));
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !candles.length) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: { background: { type: ColorType.Solid, color: '#131722' }, textColor: '#d1d4dc' },
      grid: { vertLines: { color: '#1e222d' }, horzLines: { color: '#1e222d' } },
      rightPriceScale: { borderColor: '#2b2b43' },
      timeScale: { 
        borderColor: '#2b2b43', 
        timeVisible: true, 
        secondsVisible: false, 
        rightOffset: 5, 
        barSpacing: 10,
        tickMarkFormatter: (time: UTCTimestamp, tickMarkType: number, locale: string) => {
          const date = new Date(time * 1000);
          const opts: Intl.DateTimeFormatOptions = { timeZone: 'America/New_York' };
          if (tickMarkType === 0) opts.year = 'numeric';
          else if (tickMarkType === 1) opts.month = 'short';
          else if (tickMarkType === 2) opts.day = 'numeric';
          else if (tickMarkType === 3) { opts.hour = 'numeric'; opts.minute = '2-digit'; opts.hour12 = true; }
          else if (tickMarkType === 4) { opts.hour = 'numeric'; opts.minute = '2-digit'; opts.second = '2-digit'; opts.hour12 = true; }
          return new Intl.DateTimeFormat(locale, opts).format(date);
        }
      },
      localization: {
        timeFormatter: (time: UTCTimestamp) => {
          const date = new Date(time * 1000);
          return new Intl.DateTimeFormat(undefined, { 
            timeZone: 'America/New_York', 
            year: 'numeric', month: 'short', day: 'numeric', 
            hour: 'numeric', minute: '2-digit', hour12: true 
          }).format(date);
        }
      },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: '#758696', style: LineStyle.Dashed, labelBackgroundColor: '#758696' }, horzLine: { color: '#758696', style: LineStyle.Dashed, labelBackgroundColor: '#758696' } }
    });
    chartRef.current = chart;

    const chartData = candles.map((candle) => ({
      time: candle.time as UTCTimestamp,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      value: candle.close
    }));

    const activeOscillators = indicators.filter(i => ['rsi', 'stoch_rsi', 'macd', 'ao', 'mfi', 'dmi'].includes(i.type) && i.visible);
    const volHeight = 0.10; 
    
    const totalOscHeight = Math.min(activeOscillators.length * 0.18, 0.70);
    const oscHeight = activeOscillators.length > 0 ? totalOscHeight / activeOscillators.length : 0;
    const mainBottomMargin = totalOscHeight + volHeight + 0.02; 

    chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.05, bottom: mainBottomMargin } });

    const sessionSeries = chart.addSeries(HistogramSeries, { 
      priceScaleId: 'sessions', 
      lastValueVisible: false, 
      priceLineVisible: false,
      baseLineVisible: false,
    });
    chart.priceScale('sessions').applyOptions({ visible: false, scaleMargins: { top: 0, bottom: 0 } });

    let sessionIndex = 0;
    let lastPeriod = -1;
    const isIntraday = ['1m', '5m', '15m', '1h'].includes(interval);
    const isDaily = interval === '1d';

    const nyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    
    const nyDayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      day: 'numeric',
    });

    const sessionData = candles.map((c, i) => {
      const date = new Date(c.time * 1000);
      let color = 'rgba(0, 0, 0, 0)';

      if (isIntraday) {
        let currentPeriod = parseInt(nyDayFormatter.format(date), 10);
        let forceBreak = false;
        const gapHours = i > 0 ? (c.time - candles[i-1].time) / 3600 : 0;
        if (gapHours > 12) forceBreak = true;

        if (lastPeriod === -1) {
          lastPeriod = currentPeriod;
        } else if (currentPeriod !== lastPeriod || forceBreak) {
          sessionIndex = (sessionIndex + 1) % 2;
          lastPeriod = currentPeriod;
        }

        color = sessionIndex === 0 ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0)';

        if (extendedHours) {
          const timeStr = nyFormatter.format(date);
          const parts = timeStr.split(':');
          let h = parseInt(parts[0], 10);
          if (h === 24) h = 0;
          const m = parseInt(parts[1] || '0', 10);
          const timeFloat = h + (m / 60);

          if (timeFloat >= 4 && timeFloat < 9.5) {
            color = 'rgba(33, 150, 243, 0.04)'; // Pre-market blue
          } else if (timeFloat >= 16 && timeFloat < 20) {
            color = 'rgba(255, 152, 0, 0.04)'; // Post-market orange
          }
        }
      }

      return {
        time: c.time as UTCTimestamp,
        value: 1,
        color
      };
    });
    sessionSeries.setData(sessionData);

    let mainSeries: any;
    if (chartType === 'candlestick') {
      mainSeries = chart.addSeries(CandlestickSeries, { upColor: '#26a69a', downColor: '#ef5350', borderVisible: false, wickUpColor: '#26a69a', wickDownColor: '#ef5350' });
      mainSeries.setData(chartData);
    } else if (chartType === 'line') {
      mainSeries = chart.addSeries(LineSeries, { color: '#4cc9f0', lineWidth: 2 });
      mainSeries.setData(chartData.map(d => ({ time: d.time, value: d.value })));
    } else if (chartType === 'area') {
      mainSeries = chart.addSeries(AreaSeries, { lineColor: '#4cc9f0', topColor: 'rgba(76, 201, 240, 0.4)', bottomColor: 'rgba(76, 201, 240, 0.0)' });
      mainSeries.setData(chartData.map(d => ({ time: d.time, value: d.value })));
    } else if (chartType === 'bar') {
      mainSeries = chart.addSeries(BarSeries, { upColor: '#26a69a', downColor: '#ef5350' });
      mainSeries.setData(chartData);
    } else if (chartType === 'hollow') {
      mainSeries = chart.addSeries(CandlestickSeries, { upColor: '#131722', downColor: '#ef5350', borderVisible: true, borderUpColor: '#26a69a', borderDownColor: '#ef5350', wickUpColor: '#26a69a', wickDownColor: '#ef5350' });
      mainSeries.setData(chartData);
    } else if (chartType === 'volume_candles') {
      mainSeries = chart.addSeries(CandlestickSeries, { borderVisible: false });
      const volSmaPeriod = 50;
      let sum = 0;
      mainSeries.setData(chartData.map((d, i) => {
        const vol = candles[i].volume;
        sum += vol;
        if (i >= volSmaPeriod) {
          sum -= candles[i - volSmaPeriod].volume;
        }
        const avg = i >= volSmaPeriod ? sum / volSmaPeriod : sum / (i + 1);
        
        // If volume is 2x the average, it's fully opaque (1.0). Minimum opacity is 0.15.
        const ratio = avg > 0 ? vol / (avg * 2) : 0.5;
        const opacity = Math.min(1, Math.max(0.15, ratio));
        
        const color = d.close >= d.open ? `rgba(38, 166, 154, ${opacity.toFixed(2)})` : `rgba(239, 83, 80, ${opacity.toFixed(2)})`;
        return { ...d, color, wickColor: color, borderColor: color };
      }));
    } else if (chartType === 'heikin_ashi') {
      mainSeries = chart.addSeries(CandlestickSeries, { upColor: '#26a69a', downColor: '#ef5350', borderVisible: false, wickUpColor: '#26a69a', wickDownColor: '#ef5350' });
      const haData: any[] = [];
      let prevOpen = candles[0].open;
      let prevClose = candles[0].close;
      chartData.forEach((d, i) => {
        const close = (candles[i].open + candles[i].high + candles[i].low + candles[i].close) / 4;
        const open = i === 0 ? (candles[0].open + candles[0].close) / 2 : (prevOpen + prevClose) / 2;
        const high = Math.max(candles[i].high, open, close);
        const low = Math.min(candles[i].low, open, close);
        haData.push({ time: d.time, open, high, low, close });
        prevOpen = open;
        prevClose = close;
      });
      mainSeries.setData(haData);
    }

    if (analysis && analysis.support20 && analysis.resistance20 && ['candlestick', 'hollow', 'bar', 'heikin_ashi'].includes(chartType)) {
      mainSeries.createPriceLine({ price: analysis.support20, color: '#2c7a63', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: 'S20' });
      mainSeries.createPriceLine({ price: analysis.resistance20, color: '#9a4e56', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: 'R20' });
    }

    mainSeriesRef.current = mainSeries;

    const volumeSeries = chart.addSeries(HistogramSeries, { color: '#26a69a', priceFormat: { type: 'volume' }, priceScaleId: '' });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: Math.max(0, 1 - mainBottomMargin + 0.01), bottom: Math.max(0, totalOscHeight + 0.01) } });
    volumeSeries.setData(candles.map((candle) => ({
      time: candle.time as UTCTimestamp,
      value: candle.volume,
      color: candle.close >= candle.open ? 'rgba(38, 166, 154, 0.8)' : 'rgba(239, 83, 80, 0.8)',
    })));

    let oscIndex = 0;
    const allSupertrendMarkers: any[] = [];
    indicators.forEach((ind) => {
      if (!ind.visible) return;
      const s = ind.settings;

      if (ind.type === 'ema') {
        chart.addSeries(LineSeries, { color: s.color, lineWidth: 2, title: `EMA ${s.period}` }).setData(emaSeries(candles, s.period));
      } else if (ind.type === 'sma') {
        chart.addSeries(LineSeries, { color: s.color, lineWidth: 2, title: `SMA ${s.period}` }).setData(smaSeries(candles, s.period));
      } else if (ind.type === 'vwap') {
        chart.addSeries(LineSeries, { color: s.color, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'VWAP' }).setData(vwapSeriesData(candles));
      } else if (ind.type === 'bollinger') {
        const { upper, lower, middle } = bollingerBandsData(candles, s.period, s.stdDev);
        chart.addSeries(LineSeries, { color: s.color, lineWidth: 1 }).setData(upper);
        chart.addSeries(LineSeries, { color: s.color, lineWidth: 1 }).setData(lower);
        chart.addSeries(LineSeries, { color: s.color, lineWidth: 1, lineStyle: LineStyle.Dashed }).setData(middle);
      } else if (ind.type === 'supertrend') {
        const stData = supertrendData(candles, s.atrPeriod, s.multiplier, s.upColor, s.downColor);
        chart.addSeries(LineSeries, { color: s.upColor, lineWidth: 2, title: `ST ${s.atrPeriod}` }).setData(stData.line);
        allSupertrendMarkers.push(...stData.markers);
      } else {
        const scaleId = `osc_${oscIndex}`;
        const bottomM = Math.max(0, oscHeight * (activeOscillators.length - 1 - oscIndex));
        const topM = Math.max(0, 1 - (bottomM + oscHeight) + 0.01);
        oscIndex++;

        const setupOscScale = (series: any) => series.priceScale().applyOptions({ scaleMargins: { top: topM, bottom: bottomM } });

        if (ind.type === 'rsi') {
          const series = chart.addSeries(LineSeries, { color: s.color, lineWidth: 2, priceScaleId: scaleId });
          setupOscScale(series);
          series.setData(rsiData(candles, s.period));
          series.createPriceLine({ price: s.overbought, color: 'rgba(255,255,255,0.2)', lineWidth: 1, lineStyle: LineStyle.Dashed });
          series.createPriceLine({ price: s.oversold, color: 'rgba(255,255,255,0.2)', lineWidth: 1, lineStyle: LineStyle.Dashed });
        } else if (ind.type === 'macd') {
          const hist = chart.addSeries(HistogramSeries, { priceScaleId: scaleId });
          const macdLine = chart.addSeries(LineSeries, { color: s.macdColor, lineWidth: 2, priceScaleId: scaleId });
          const sigLine = chart.addSeries(LineSeries, { color: s.signalColor, lineWidth: 2, priceScaleId: scaleId });
          setupOscScale(hist);
          const data = macdData(candles, s.fast, s.slow, s.signal);
          hist.setData(data.histogram);
          macdLine.setData(data.macd);
          sigLine.setData(data.signal);
        } else if (ind.type === 'ao') {
          const hist = chart.addSeries(HistogramSeries, { priceScaleId: scaleId });
          setupOscScale(hist);
          hist.setData(awesomeOscillatorData(candles, s.fast, s.slow));
        } else if (ind.type === 'mfi') {
          const series = chart.addSeries(LineSeries, { color: s.color, lineWidth: 2, priceScaleId: scaleId });
          setupOscScale(series);
          series.setData(mfiData(candles, s.period));
          series.createPriceLine({ price: 80, color: 'rgba(255,255,255,0.2)', lineWidth: 1, lineStyle: LineStyle.Dashed });
          series.createPriceLine({ price: 20, color: 'rgba(255,255,255,0.2)', lineWidth: 1, lineStyle: LineStyle.Dashed });
        } else if (ind.type === 'stoch_rsi') {
          const kLine = chart.addSeries(LineSeries, { color: s.kColor, lineWidth: 2, priceScaleId: scaleId });
          const dLine = chart.addSeries(LineSeries, { color: s.dColor, lineWidth: 2, priceScaleId: scaleId });
          setupOscScale(kLine);
          const data = stochasticRsiData(candles, s.rsiPeriod, s.stochPeriod, s.k, s.d);
          kLine.setData(data.k);
          dLine.setData(data.d);
        } else if (ind.type === 'dmi') {
          const pLine = chart.addSeries(LineSeries, { color: s.plusColor, lineWidth: 2, priceScaleId: scaleId });
          const mLine = chart.addSeries(LineSeries, { color: s.minusColor, lineWidth: 2, priceScaleId: scaleId });
          const aLine = chart.addSeries(LineSeries, { color: s.adxColor, lineWidth: 2, priceScaleId: scaleId });
          setupOscScale(pLine);
          const data = dmiData(candles, s.period);
          pLine.setData(data.plusDI);
          mLine.setData(data.minusDI);
          aLine.setData(data.adx);
        }
      }
    });

    supertrendMarkersRef.current = allSupertrendMarkers;
    supertrendMarkersRef.current = allSupertrendMarkers;

    chart.timeScale().setVisibleLogicalRange({
      from: Math.max(0, candles.length - 100),
      to: candles.length + 5,
    });

    chart.subscribeCrosshairMove((param) => {
      if (!param.time || param.point === undefined || param.point.x < 0 || param.point.y < 0) {
        setHoveredCandle(null);
      } else {
        const time = param.time as UTCTimestamp;
        const candle = candles.find(c => c.time === time);
        if (candle) setHoveredCandle(candle);
      }
    });
    const observer = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });
    });
    observer.observe(container);
    return () => { observer.disconnect(); chart.remove(); };
  }, [analysis, candles, symbol, chartType, indicators]);

  const displayCandle = hoveredCandle || (candles.length ? candles[candles.length - 1] : null);
  const colorClass = displayCandle && displayCandle.close >= displayCandle.open ? 'val-up' : 'val-down';
  const activeOscillatorsCount = indicators.filter(i => ['rsi', 'stoch_rsi', 'macd', 'ao', 'mfi', 'dmi'].includes(i.type) && i.visible).length;
  const dynamicMinHeight = 540 + (activeOscillatorsCount * 150);

  const setRange = (days: number) => {
    if (!chartRef.current || !candles.length) return;
    if (days === 0) {
      chartRef.current.timeScale().fitContent();
      return;
    }
    const lastTime = candles[candles.length - 1].time as number;
    const fromTime = lastTime - (days * 24 * 60 * 60);
    chartRef.current.timeScale().setVisibleLogicalRange({
      from: chartRef.current.timeScale().coordinateToLogical(0) || 0, // Fallback, not exact but close
      to: candles.length - 1
    });
    // For lightweight charts, it's better to find the index:
    const fromIdx = candles.findIndex(c => (c.time as number) >= fromTime);
    if (fromIdx !== -1) {
      chartRef.current.timeScale().setVisibleLogicalRange({ from: fromIdx, to: candles.length - 1 });
    }
  };

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const node = document.getElementById('header-chart-controls');
    setPortalNode(node);
  }, []);

  const topbarContent = (
    <div className="tv-topbar" style={{ flex: 1, borderBottom: 'none', padding: 0, height: 'auto', background: 'transparent' }}>
      <div className="tv-topbar-left">
        <div className="tv-symbol-search">{symbol}</div>
        <div className="tv-divider" />
        <div className="tv-timeframes">
          {INTERVAL_OPTIONS.map((item) => (
            <button 
              key={item.interval} 
              className={interval === item.interval ? 'active' : ''} 
              onClick={() => onIntervalChange?.(item.interval)}
            >
              {item.label}
            </button>
          ))}
          {['1m', '5m', '15m', '1h'].includes(interval) && (
            <button 
              className={extendedHours ? 'active' : ''} 
              onClick={() => onExtendedHoursChange?.(!extendedHours)}
              title="Toggle Extended Trading Hours"
              style={{ marginLeft: '8px', color: extendedHours ? '#4cc9f0' : '#787b86' }}
            >
              EXT
            </button>
          )}
        </div>
        <div className="tv-divider" />
        <select className="tv-select" value={chartType} onChange={(e) => setChartType(e.target.value as ChartType)}>
          <option value="candlestick">Candles</option>
          <option value="line">Line</option>
          <option value="area">Area</option>
          <option value="bar">Bars</option>
          <option value="hollow">Hollow Candles</option>
          <option value="volume_candles">Volume Candles</option>
          <option value="heikin_ashi">Heikin Ashi</option>
        </select>
        <div className="tv-divider" />
        <select className="tv-select" onChange={(e) => { if (e.target.value) { addIndicator(e.target.value as IndicatorType); e.target.value = ''; } }} value="">
          <option value="" disabled>Indicators</option>
          <option value="sma">SMA</option>
          <option value="ema">EMA</option>
          <option value="vwap">VWAP</option>
          <option value="bollinger">Bollinger Bands</option>
          <option value="supertrend">Supertrend</option>
          <option value="rsi">RSI</option>
          <option value="macd">MACD</option>
          <option value="stoch_rsi">Stoch RSI</option>
          <option value="mfi">MFI</option>
          <option value="ao">Awesome Osc</option>
          <option value="dmi">DMI</option>
        </select>
        
        <div className="tv-divider" />
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: showPatterns ? '#d1d4dc' : '#b2b5be' }}>
          <input 
            type="checkbox" 
            checked={showPatterns} 
            onChange={(e) => setShowPatterns(e.target.checked)} 
            style={{ cursor: 'pointer' }}
          />
          Patterns
        </label>
      </div>

      <div className="tv-topbar-right">
        <button className="tv-icon-btn" onClick={toggleFullscreen} title="Fullscreen">⛶</button>
      </div>
    </div>
  );

  return (
    <div ref={wrapperRef} className="tv-superchart" style={{ position: 'relative', minHeight: `${dynamicMinHeight}px` }}>
      {portalNode ? createPortal(topbarContent, portalNode) : topbarContent}

      <div className="tv-middle">
        {/* LEFT TOOLBAR */}
        <div className="tv-leftbar">
          <button className="tv-tool-btn active" title="Crosshair">
            <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" fillRule="evenodd" d="M13.5 13.5v-7h1v7h7v1h-7v7h-1v-7h-7v-1h7z"/></svg>
          </button>
          <button className="tv-tool-btn" title="Trend Line">
            <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" d="M21 7.5a2.5 2.5 0 10-5 0 2.5 2.5 0 005 0zm-14 13a2.5 2.5 0 10-5 0 2.5 2.5 0 005 0zm11.207-9.793l-8 8-1.414-1.414 8-8 1.414 1.414z"/></svg>
          </button>
          <button className="tv-tool-btn" title="Fib Retracement">
            <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" d="M5 8h18v1H5V8zm0 5h18v1H5v-1zm0 5h18v1H5v-1z"/></svg>
          </button>
          <button className="tv-tool-btn" title="Text">
            <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" d="M10 8h8v1h-3.5v11h-1V9H10V8z"/></svg>
          </button>
          <div className="tv-leftbar-spacer" />
          <button className="tv-tool-btn" title="Magnet Mode">
            <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" d="M17 11V8a3 3 0 0 0-6 0v3H9V8a5 5 0 0 1 10 0v3h-2zM9 13v6h4v-6H9zm6 0v6h4v-6h-4z"/></svg>
          </button>
        </div>

        {/* MAIN CHART AREA */}
        <div className="tv-main">
          <div className="trading-chart-wrapper">
            <div className="chart-legend-area">
              {displayCandle && (
                <div className="chart-legend-ohlcv">
                  <strong className="legend-symbol">{symbol}</strong>
                  <span>O <span className={colorClass}>{displayCandle.open.toFixed(2)}</span></span>
                  <span>H <span className={colorClass}>{displayCandle.high.toFixed(2)}</span></span>
                  <span>L <span className={colorClass}>{displayCandle.low.toFixed(2)}</span></span>
                  <span>C <span className={colorClass}>{displayCandle.close.toFixed(2)}</span></span>
                  <span>Vol <span className="val-neutral">{(displayCandle.volume >= 1000000 ? (displayCandle.volume / 1000000).toFixed(2) + 'M' : displayCandle.volume >= 1000 ? (displayCandle.volume / 1000).toFixed(2) + 'K' : displayCandle.volume)}</span></span>
                </div>
              )}

              <div className="indicator-legend-list">
                {indicators.map((ind) => (
                  <div key={ind.id} className="indicator-legend-row">
                    <div className="indicator-legend-title" style={{ color: ind.settings.color || ind.settings.macdColor || '#d1d4dc' }}>
                      <span className="indicator-legend-name">{ind.type.toUpperCase()}</span>
                      {ind.settings.period && <span className="indicator-legend-period">{ind.settings.period}</span>}
                      {ind.type === 'macd' && <span className="indicator-legend-period">{ind.settings.fast},{ind.settings.slow},{ind.settings.signal}</span>}
                    </div>
                    <div className="indicator-actions">
                      <button className={ind.visible ? 'action-btn' : 'action-btn off'} onClick={() => setIndicators(indicators.map(i => i.id === ind.id ? { ...i, visible: !i.visible } : i))} title="Toggle Visibility">👁</button>
                      <button className="action-btn" onClick={() => setEditingIndicatorId(editingIndicatorId === ind.id ? null : ind.id)} title="Settings">⚙</button>
                      <button className="action-btn" onClick={() => removeIndicator(ind.id)} title="Remove">✖</button>
                    </div>

                    {editingIndicatorId === ind.id && (
                      <div className="indicator-settings-popover">
                        <div className="settings-header">
                          <h4>{ind.type.toUpperCase()} Settings</h4>
                          <button className="close-btn" onClick={() => setEditingIndicatorId(null)}>✖</button>
                        </div>
                        <div className="settings-body" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {Object.entries(ind.settings).map(([key, val]) => {
                            const isColor = key.toLowerCase().includes('color');
                            const type = isColor ? 'color' : 'number';
                            return (
                              <label key={key}>
                                <span>{key}</span>
                                <input
                                  type={type}
                                  value={val as string | number}
                                  onChange={(e) => updateSettings(ind.id, { [key]: isColor ? e.target.value : (parseFloat(e.target.value) || 0) })}
                                />
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* The Lightweight Chart Canvas Container */}
            <div ref={containerRef} className="chart-container" aria-label={`${symbol} price chart`} style={{ width: '100%', height: '100%' }} />
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="tv-rightbar">
          <div className="tv-rightbar-tabs">
            <button className="active" title="Watchlist">
              <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" d="M18 10h-2v2h2v-2zm-4 0h-2v2h2v-2zm-4 0H8v2h2v-2zm8 4h-2v2h2v-2zm-4 0h-2v2h2v-2zm-4 0H8v2h2v-2zm8 4h-2v2h2v-2zm-4 0h-2v2h2v-2zm-4 0H8v2h2v-2zM7 7h14v14H7V7z"/></svg>
            </button>
            <button title="Alerts">
              <svg viewBox="0 0 28 28" width="28" height="28"><path fill="currentColor" d="M14 6a4.5 4.5 0 0 0-4.5 4.5V13l-2 3v1h13v-1l-2-3v-2.5A4.5 4.5 0 0 0 14 6zm0 13a2.5 2.5 0 0 1-2.5-2.5h5A2.5 2.5 0 0 1 14 19z"/></svg>
            </button>
          </div>
          <div className="tv-watchlist">
            <div className="wl-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Watchlist</span>
              </div>
              <div style={{ marginTop: '8px', position: 'relative' }}>
                <input 
                  type="text" 
                  value={newSymbol} 
                  onChange={e => {
                    setNewSymbol(e.target.value.toUpperCase());
                    setShowDropdown(true);
                  }} 
                  onKeyDown={e => { 
                    if (e.key === 'Enter' && newSymbol.trim()) { 
                      onAddSymbol?.(newSymbol.trim()); 
                      setNewSymbol('');
                      setShowDropdown(false);
                    } 
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                  placeholder="+ Add Symbol" 
                  className="tv-add-sym-input"
                />
                {showDropdown && (newSymbol.trim() !== '') && (
                  <div className="search-dropdown">
                    {isSearching && searchResults.length === 0 ? (
                      <div className="search-item val-neutral">Searching...</div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map(res => (
                        <div 
                          key={res.symbol} 
                          className="search-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onAddSymbol?.(res.symbol);
                            setNewSymbol('');
                            setShowDropdown(false);
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', overflow: 'hidden' }}>
                            <strong>{res.symbol}</strong>
                            <small className="val-neutral" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {res.shortname || res.longname}
                            </small>
                          </div>
                          <small className="val-neutral" style={{ flexShrink: 0, fontSize: '0.7em' }}>{res.typeDisp}</small>
                        </div>
                      ))
                    ) : !isSearching ? (
                      <div className="search-item val-neutral">No results found</div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <div className="wl-list">
              {watchlistSymbols.map(sym => {
                const snap = snapshots[sym];
                const c = snap?.data?.candles;
                const lastPrice = c?.length ? c[c.length - 1].close : 0;
                const prevPrice = c?.length > 1 ? c[c.length - 2].close : lastPrice;
                const colorClass = lastPrice >= prevPrice ? 'val-up' : 'val-down';
                const isActive = sym === symbol;
                
                return (
                  <div key={sym} className={`wl-item ${isActive ? 'active' : ''}`} onClick={() => onSelectSymbol?.(sym)}>
                    <span className="wl-sym">{sym}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`wl-price ${colorClass}`}>{lastPrice ? lastPrice.toFixed(2) : '...'}</span>
                      <button 
                        className="wl-del-btn" 
                        onClick={(e) => { e.stopPropagation(); onRemoveSymbol?.(sym); }}
                        title="Remove"
                      >✖</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM TOOLBAR */}
      <div className="tv-bottombar">
        <div className="tv-ranges">
          <button onClick={() => setRange(1)}>1D</button>
          <button onClick={() => setRange(5)}>5D</button>
          <button onClick={() => setRange(30)}>1M</button>
          <button onClick={() => setRange(90)}>3M</button>
          <button onClick={() => setRange(180)}>6M</button>
          <button onClick={() => setRange(365)}>1Y</button>
          <button onClick={() => setRange(0)}>All</button>
        </div>
        <div className="tv-bottom-right">
          <span>ext</span>
          <span>%</span>
          <span>log</span>
          <span>auto</span>
        </div>
      </div>
    </div>
  );
}
